<script setup lang="ts">
  import { useQueryClient } from '@tanstack/vue-query';
  import { computed, ref } from 'vue';

  import type { Partner, User } from '@stuut/api/models';
  import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
  } from '@ui/components/alert-dialog';
  import { Button } from '@ui/components/button';
  import { toast } from '@ui/components/sonner';
  import { Switch } from '@ui/components/switch';

  import { stuut } from '@/api';
  import { AssigneeSelect } from '@/components/users';

  interface Props {
    partner: Partner;
    showAvatarOnly?: boolean;
  }

  const props = defineProps<Props>();

  const isUpdating = ref(false);
  const selectedUser = ref<User | null>(null);
  const showPartnerAssignModal = ref(false);
  const queryClient = useQueryClient();

  const isAllOpenTasks = ref(false);
  const isAllAssigned = ref(false);
  const isAllLastAssignee = ref(false);
  const isAllUnassigned = ref(false);

  const forceTaskTableRefresh = () => {
    queryClient.invalidateQueries({
      exact: false,
      queryKey: ['tasks'],
    });
  };

  const assignUser = async () => {
    if (isUpdating.value) return;

    isUpdating.value = true;
    showPartnerAssignModal.value = false;

    try {
      await stuut.partners.reassign(
        props.partner.id,
        {
          all_assigned: isAllAssigned.value,
          all_open: isAllOpenTasks.value,
          all_previous_assignee: isAllLastAssignee.value,
          all_unassigned: isAllUnassigned.value,
          assignee_id: selectedUser.value?.id || null,
        },
        props.partner,
      );

      const successMessage = selectedUser.value
        ? `Successfully assigned.`
        : `Successfully removed assignment`;

      toast.success(successMessage);

      forceTaskTableRefresh();
      resetToggles();
    } catch (err: any) {
      toast.error(`Failed to assign with error: ${err}`);
    } finally {
      isUpdating.value = false;
    }
  };

  const openPartnerAssignModal = (user: User | null) => {
    resetToggles();

    selectedUser.value = user;
    showPartnerAssignModal.value = true;
  };

  const assignmentDialogDescription = computed(() => {
    return selectedUser.value
      ? `Assign ${props.partner.computedName} to ${selectedUser.value?.name}?`
      : `Remove assignment for ${props.partner.computedName}?`;
  });

  const resetToggles = () => {
    isAllOpenTasks.value = false;
    isAllAssigned.value = false;
    isAllLastAssignee.value = false;
    isAllUnassigned.value = false;
  };

  const updateAllTasks = (value) => {
    isAllOpenTasks.value = value;
    isAllUnassigned.value = value;
    isAllLastAssignee.value = value;
    isAllAssigned.value = value;
  };

  const verifyAllTasks = () => {
    if (isAllOpenTasks.value) return;

    if (
      selectedUser.value &&
      isAllAssigned.value &&
      isAllLastAssignee.value &&
      isAllUnassigned.value
    ) {
      isAllOpenTasks.value = true;
    }

    if (!selectedUser.value && isAllAssigned.value && isAllLastAssignee.value) {
      isAllOpenTasks.value = true;
    }
  };

  const updateAllAssigned = (value) => {
    isAllOpenTasks.value = false;
    isAllAssigned.value = value;

    if (value) isAllLastAssignee.value = true;

    verifyAllTasks();
  };

  const updateAnyPreviousAssignee = (value) => {
    isAllOpenTasks.value = false;
    isAllLastAssignee.value = value;

    if (value === false) {
      isAllAssigned.value = false;
    }

    verifyAllTasks();
  };

  const updateAllUnassigned = (value) => {
    isAllOpenTasks.value = false;
    isAllUnassigned.value = value;

    verifyAllTasks();
  };
</script>

<template>
  <AssigneeSelect
    :assignee="partner.assignee"
    :show-avatar-only="showAvatarOnly"
    @selected="
      (user) => {
        openPartnerAssignModal(user);
      }
    "
  />

  <AlertDialog v-if="showPartnerAssignModal" v-model:open="showPartnerAssignModal">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle> Confirm </AlertDialogTitle>
      </AlertDialogHeader>

      <AlertDialogDescription class="space-y-6">
        <div class="space-y-2.5">
          <p class="text-base text-foreground">
            {{ assignmentDialogDescription }}
          </p>

          <p v-if="selectedUser?.name">
            Future tasks generated for
            <span class="font-semibold">{{ partner.computedName }}</span> will be auto-assigned to
            <span class="font-semibold">{{ selectedUser?.name }}</span
            >.
          </p>

          <p v-else>
            There will be no automatic assignment for future tasks generated for
            {{ partner.computedName }}.
          </p>
        </div>

        <div class="border-t pt-4 space-y-6">
          <div class="space-y-2">
            <strong>Task Reassignment</strong>

            <p v-if="selectedUser?.name">
              Options to update existing tasks' assignee to
              <span class="font-semibold">{{ selectedUser?.name }}</span>
            </p>

            <p v-else>Options to remove assignments</p>
          </div>

          <div class="bg-background-muted px-4 py-5 rounded space-y-4">
            <div class="space-y-2">
              <strong class="text-muted-foreground"> All tasks </strong>

              <div class="flex items-center gap-x-3">
                <Switch
                  :model-value="isAllOpenTasks"
                  :disabled="isUpdating"
                  @update:model-value="updateAllTasks"
                />

                <span v-if="selectedUser?.name"> Assign all tasks </span>
                <span v-else> Unassign all tasks </span>
              </div>
            </div>

            <div class="space-y-2">
              <strong class="text-muted-foreground"> Tasks with an assignee </strong>

              <div class="flex items-center gap-x-3">
                <Switch
                  :model-value="isAllAssigned"
                  :disabled="isUpdating"
                  @update:model-value="updateAllAssigned"
                />

                <span> With any assignee </span>
              </div>

              <div v-if="partner.assignee?.id" class="flex items-center gap-x-3">
                <Switch
                  :model-value="isAllLastAssignee"
                  :disabled="isUpdating"
                  @update:model-value="updateAnyPreviousAssignee"
                />

                <span> With previous assignee ({{ partner?.assignee?.name }}) </span>
              </div>
            </div>

            <div v-if="selectedUser?.name" class="space-y-2">
              <strong class="text-muted-foreground"> Unassigned tasks </strong>

              <div class="flex items-center gap-x-3">
                <Switch
                  :model-value="isAllUnassigned"
                  :disabled="isUpdating"
                  @update:model-value="updateAllUnassigned"
                />

                <span> All unassigned </span>
              </div>
            </div>
          </div>
        </div>
      </AlertDialogDescription>

      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <Button @click="assignUser()">Confirm</Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>

<style scoped></style>
